import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  FaRegClock,
  FaMapMarkerAlt,
  FaEdit,
  FaTrashAlt,
  FaBell,
  FaShareAlt,
  FaUserFriends,
  FaRegCalendarAlt,
  FaCircle,
  FaChevronDown,
  FaChevronUp,
  FaCheck,
  FaClock,
  FaCalendarAlt,
  FaQuestion,
  FaTimes,
} from 'react-icons/fa';
import userService from '../../../services/userService';

// Flag to disable notifications/websockets - should match other files
const NOTIFICATIONS_ENABLED = false;

// Suppress WebSocket errors if notifications are disabled
if (!NOTIFICATIONS_ENABLED) {
  // Override WebSocket constructor to prevent connections when disabled
  if (typeof window !== 'undefined') {
    const originalWebSocket = window.WebSocket;
    window.WebSocket = function(url, protocols) {
      // Block connections to localhost:3000/ws when disabled
      if (url && url.includes('localhost:3000/ws')) {
        // Return mock WebSocket that does nothing
        return {
          url,
          readyState: 3, // CLOSED
          send: () => {},
          close: () => {},
          onopen: null,
          onclose: null,
          onerror: null,
          onmessage: null
        };
      }
      // For other WebSocket connections, use the original constructor
      return new originalWebSocket(url, protocols);
    };
    // Copy static properties from the original WebSocket
    Object.keys(originalWebSocket).forEach(key => {
      window.WebSocket[key] = originalWebSocket[key];
    });
  }
}

const EventDetailsDialog = ({ event, onClose, onEdit, onDelete, onSetReminder, onShare, isShared = false }) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isSharersExpanded, setIsSharersExpanded] = useState(true); // Default expanded
  const [sharedUsersData, setSharedUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const processedEventIds = useRef(new Set());
  
  // Get standardized event times
  const eventStartTime = event?.startTime || event?.start;
  const eventEndTime = event?.endTime || event?.end;
  
  // Check if the current user is the event owner - enhanced to handle object userIds
  const isEventOwner = event && user && (() => {
    if (!event || !user) return false;
    
    // Debug ownership data
    console.log('Checking event ownership:', { 
      eventId: event.id,
      userId: user.id, 
      eventUserId: event.userId,
      createdBy: event.createdBy,
      ownerId: event.ownerId
    });
    
    // Normal string comparison case
    if (typeof event.userId === 'string' && event.userId === user.id) {
      return true;
    }
    
    // Handle object userId case
    if (typeof event.userId === 'object' && event.userId) {
      if (event.userId.id === user.id) return true;
      if (event.userId._id === user.id) return true;
    }
    
    // Additional checks for common owner-related fields
    if (event.createdBy === user.id) return true;
    if (event.ownerId === user.id) return true;
    if (event.owner === user.id) return true;
    
    // Check if owner is an object
    if (event.owner && typeof event.owner === 'object') {
      if (event.owner.id === user.id || event.owner._id === user.id) return true;
    }
    
    return false;
  })();
  
  // Calculate duration
  const duration = eventStartTime && eventEndTime
    ? differenceInMinutes(parseISO(eventEndTime), parseISO(eventStartTime))
    : 0;
  
  // Format duration
  const formatDuration = () => {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return (hours > 0 ? `${hours}h ` : '') + (minutes > 0 ? `${minutes}m` : '');
  };

  // Process user data from API
  const processUserData = (apiData, userId) => {
    // Handle different API response structures
    if (!apiData) return null;
    
    // Case 1: { user: {...} }
    if (apiData.user && typeof apiData.user === 'object') {
      return {
        id: apiData.user.id || userId,
        name: apiData.user.name || apiData.user.username || apiData.user.email || formatUserId(userId),
        avatar: apiData.user.profileImage || apiData.user.profile_photo_url || apiData.user.avatar || null
      };
    }
    
    // Case 2: Direct user object
    return {
      id: apiData.id || userId,
      name: apiData.name || apiData.username || apiData.email || formatUserId(userId),
      avatar: apiData.profileImage || apiData.profile_photo_url || apiData.avatar || null
    };
  };

  const processSharedUsers = async () => {
    try {
      setIsLoading(true);
      let processedUsers = [];

      // Case 1: Current user is the event owner - display all users the event is shared with
      if (isEventOwner && event.sharedWith && Array.isArray(event.sharedWith) && event.sharedWith.length > 0) {
        console.log('Processing shared users as event owner:', event.sharedWith);
        
        processedUsers = await Promise.all(
          event.sharedWith.map(async (shared) => {
            try {
              // Handle different formats of userId in sharedWith
              const userId = typeof shared === 'object' ? (shared.userId || shared.id) : shared;
              
              if (!userId) {
                console.warn('No userId found in shared item:', shared);
                return null;
              }
              
              // Skip processing if this user ID was already processed
              if (processedEventIds.current.has(userId)) {
                console.log(`Already processed user ${userId}, skipping`);
                return null;
              }
              
              processedEventIds.current.add(userId);
              
              const userData = await userService.fetchUser(userId);
              console.log(`Fetched user data for ${userId}:`, userData);
              
              return {
                ...processUserData(userData, userId),
                status: shared.status || 'pending',
                responseMessage: shared.responseMessage || ''
              };
            } catch (err) {
              console.error('Error processing individual shared user:', err);
              return null;
            }
          })
        );
        
        // Filter out null values from failed fetches
        processedUsers = processedUsers.filter(user => user !== null);
      } 
      // Case 2: Current user is a recipient of a shared event - show the owner
      else if (!isEventOwner) {
        console.log('Processing event owner as shared recipient', event.owner || event.ownerId || event.userId || event.createdBy);
        
        // Determine owner ID from various possible fields
        let ownerId = null;
        if (typeof event.owner === 'string') {
          ownerId = event.owner;
        } else if (typeof event.owner === 'object' && event.owner) {
          ownerId = event.owner.id || event.owner._id;
        } else {
          ownerId = event.ownerId || event.createdBy || 
                   (typeof event.userId === 'string' ? event.userId : 
                   (event.userId && typeof event.userId === 'object' ? event.userId.id : null));
        }
        
        if (ownerId) {
          try {
            const ownerData = await userService.fetchUser(ownerId);
            console.log(`Fetched owner data for ${ownerId}:`, ownerData);
            
            if (ownerData) {
              const processedOwner = processUserData(ownerData, ownerId);
              processedUsers = [{
                ...processedOwner,
                status: 'owner',
                isOrganizer: true
              }];
            }
          } catch (err) {
            console.error('Error fetching event owner:', err);
          }
        }
      }

      console.log('Final processed users:', processedUsers);
      return processedUsers;
    } catch (error) {
      console.error('Error in processSharedUsers:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadSharedUsers = async () => {
      const users = await processSharedUsers();
      setSharedUsersData(users);
    };
    loadSharedUsers();
  }, [event]);

  // Format user ID for display
  const formatUserId = (userId) => {
    if (!userId) return "Unknown User";
    
    // Ensure userId is a string
    const userIdStr = String(userId);
    
    // If ID is UUID format, extract the first section
    if (userIdStr.includes('-')) {
      const parts = userIdStr.split('-');
      if (parts.length > 0) {
        return `${parts[0]}`;
      }
    }
    
    // Truncate long IDs
    return userIdStr.length > 8 
      ? `${userIdStr.substring(0, 8)}`
      : userIdStr;
  };

  // Use the fetched user data instead of trying to extract from sharedWith
  const sharedUsers = sharedUsersData.length > 0 
    ? sharedUsersData 
    : (event?.sharedWith?.length > 0 ? [{ 
        id: 'unknown-user',
        name: 'Shared User',
        avatar: null,
        status: 'pending'
      }] : []);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring", 
        damping: 25,
        staggerChildren: 0.05
      }
    },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } }
  };

  if (!event) return null;

  // Format date and time
  const formattedDate = format(parseISO(eventStartTime), "EEEE, MMMM d, yyyy");
  const formattedStartTime = format(parseISO(eventStartTime), "h:mm a");
  const formattedEndTime = format(parseISO(eventEndTime), "h:mm a");

  // Get event color with fallback
  const eventColor = event.color || 'var(--soft-pink)';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-6"
        onClick={onClose}
      >
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="w-full max-w-md bg-gradient-to-br from-gray-800/98 to-gray-900/98 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.3)] overflow-hidden relative text-pri-white"
          onClick={e => e.stopPropagation()}
        >
          {/* Enhanced glossy effects and light reflections */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/15 via-transparent to-mint-green/15" />
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-soft-pink/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-mint-green/40 to-transparent" />
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-radial from-soft-pink/20 to-transparent opacity-40 rounded-full blur-xl" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-radial from-mint-green/20 to-transparent opacity-40 rounded-full blur-xl" />
          </div>

          {/* Color bar with glow effect */}
          <div className="relative overflow-hidden">
            <div 
              className="h-4 w-full bg-soft-pink"
              style={{ backgroundColor: event.color }}
            />
            <div
              className="absolute inset-0 blur-sm"
              style={{ backgroundColor: event.color || 'var(--soft-pink)', opacity: 0.5 }}
            />
          </div>

          {/* Header with glossy effect */}
          <div className="relative px-6 py-5 border-b border-gray-700/70">
            <div className="flex justify-between items-center">
              <motion.h2 
                variants={itemVariants}
                className="text-2xl font-bold text-pri-white truncate pr-6 drop-shadow-sm"
              >
                {event.title}
              </motion.h2>
              <motion.button
                variants={itemVariants}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="text-gray-400 hover:text-pri-white transition-colors p-1.5 rounded-full bg-gray-800/80 backdrop-blur-sm"
                onClick={onClose}
              >
                <XMarkIcon className="h-5 w-5" />
              </motion.button>
            </div>
            
            {/* Shared/owner indicator */}
            {isShared && (
              <motion.div 
                variants={itemVariants}
                className="flex items-center mt-1 text-sm text-soft-pink font-medium"
              >
                <FaUserFriends className="mr-1.5 h-3.5 w-3.5" />
                <span>Shared by {event.owner?.name || 'Event Owner'}</span>
              </motion.div>
            )}
          </div>

          {/* Content */}
          <div className="relative px-6 py-5 space-y-6">
            {/* Date and time section */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4">
              <div className="mt-0.5 text-soft-pink bg-soft-pink/10 p-2.5 rounded-xl shadow-sm">
                <FaCalendarAlt className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-pri-white font-medium text-lg">{formattedDate}</div>
                <div className="text-pri-white/90 text-sm flex items-center mt-1.5">
                  <FaClock className="h-3.5 w-3.5 mr-2 text-soft-pink" />
                  <span className="font-medium">{formattedStartTime} - {formattedEndTime}</span>
                  <span className="text-xs bg-soft-pink/10 px-2.5 py-1 rounded-full ml-3 border border-soft-pink/20 text-pri-white">
                    {formatDuration()}
                  </span>
                </div>
              </div>
            </motion.div>
            
            {/* Location section - only show if location exists */}
            {event.location && (
              <motion.div variants={itemVariants} className="flex items-start space-x-4">
                <div className="mt-0.5 text-soft-pink bg-soft-pink/10 p-2.5 rounded-xl shadow-sm">
                  <FaMapMarkerAlt className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-pri-white font-medium">{event.location}</div>
                </div>
              </motion.div>
            )}
            
            {/* Description section - only show if description exists */}
            {event.description && (
              <motion.div variants={itemVariants} className="bg-gray-800/40 rounded-xl p-5 backdrop-blur-sm border border-gray-700/30 shadow-sm">
                <div className="text-pri-white/90 text-sm whitespace-pre-wrap leading-relaxed">
                  {event.description}
                </div>
              </motion.div>
            )}

            {/* Shared with section - always show if event has shared users or is shared */}
            {((event?.sharedWith?.length > 0) || isShared) && (
              <motion.div 
                variants={itemVariants} 
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl overflow-hidden backdrop-blur-sm border border-gray-700/30 shadow-sm"
              >
                <div
                  className="px-5 py-4 flex justify-between items-center cursor-pointer"
                  onClick={() => setIsSharersExpanded(!isSharersExpanded)}
                >
                  <div className="flex items-center">
                    <div className="bg-soft-pink/10 p-1.5 rounded-lg mr-3 shadow-sm">
                      <FaUserFriends className="h-4 w-4 text-soft-pink" />
                    </div>
                    <span className="text-pri-white font-medium">
                      {isShared 
                        ? 'Shared by' 
                        : `Shared with ${event.sharedWith?.length || 0} ${event.sharedWith?.length === 1 ? 'person' : 'people'}`}
                    </span>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="text-gray-400 bg-gray-800/60 p-1.5 rounded-lg"
                  >
                    {isSharersExpanded ? (
                      <FaChevronUp className="h-3 w-3" />
                    ) : (
                      <FaChevronDown className="h-3 w-3" />
                    )}
                  </motion.div>
                </div>
                
                <AnimatePresence>
                  {isSharersExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ type: "spring", damping: 30, stiffness: 300 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 space-y-3">
                        {isLoading ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-pulse flex space-x-3 items-center">
                              <div className="h-2.5 w-2.5 bg-soft-pink rounded-full"></div>
                              <div className="h-2.5 w-2.5 bg-soft-pink rounded-full delay-75"></div>
                              <div className="h-2.5 w-2.5 bg-soft-pink rounded-full delay-150"></div>
                            </div>
                          </div>
                        ) : sharedUsers.length > 0 ? (
                          <div className="flex flex-wrap gap-3 justify-center py-2">
                            {sharedUsers.map((user, index) => (
                              <div key={user.id || index} className="relative group">
                                {/* Special styling for owner */}
                                <div className="relative">
                                  <div className={`h-14 w-14 rounded-full flex items-center justify-center shadow-lg
                                    ${user.status === 'owner' || user.isSender || user.isOrganizer 
                                      ? 'bg-gradient-to-br from-amber-400/70 to-amber-600/70 ring-2 ring-amber-300 group-hover:ring-amber-400' 
                                      : 'bg-gradient-to-br from-gray-700 to-gray-800 ring-2 ring-gray-600 group-hover:ring-soft-pink/50'} 
                                    transition-all overflow-hidden`}>
                                    {user.avatar ? (
                                      <img 
                                        src={user.avatar} 
                                        alt={user.name || 'User'} 
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                          e.target.src = ""; // Clear source on error
                                          e.target.onerror = null; // Prevent infinite error loop
                                          e.target.parentNode.classList.add("fallback-avatar");
                                          const letterElement = document.createElement("div");
                                          letterElement.className = "text-pri-white text-lg font-bold";
                                          letterElement.textContent = ((user.name || 'U')[0] || "U").toUpperCase();
                                          e.target.parentNode.appendChild(letterElement);
                                        }}
                                      />
                                    ) : (
                                      <div className="text-pri-white text-xl font-bold">
                                        {((user.name || 'U')[0] || "U").toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Status indicator in bottom right - different for owner */}
                                  {(() => {
                                    // Special owner indicator
                                    if (user.status === 'owner' || user.isSender || user.isOrganizer) {
                                      return (
                                        <div className="absolute -bottom-2 -right-2 h-7 w-7 rounded-full bg-amber-500 flex items-center justify-center shadow-xl z-30 border-2 border-gray-900">
                                          <FaRegCalendarAlt className="h-4 w-4 text-white" />
                                        </div>
                                      );
                                    }
                                    
                                    // Regular status indicators for recipients
                                    let status = user.status || 'accepted'; // Default to accepted for visibility
                                    
                                    if (!isShared && event.sharedWith) {
                                      const shareInfo = Array.isArray(event.sharedWith) 
                                        ? event.sharedWith.find(s => 
                                            (typeof s === 'object' && s !== null && 
                                              ((s.userId && s.userId === user.id) || 
                                              (s.id && s.id === user.id)))
                                          )
                                        : null;
                                      
                                      if (shareInfo) {
                                        status = shareInfo.status || user.status || 'accepted';
                                      }
                                    }
                                    
                                    switch(status) {
                                      case 'pending':
                                        return (
                                          <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-gray-600 flex items-center justify-center shadow-lg z-20 border-2 border-gray-900">
                                            <FaQuestion className="h-2.5 w-2.5 text-pri-white" />
                                          </div>
                                        );
                                      case 'accepted':
                                        return (
                                          <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center shadow-lg z-20 border-2 border-gray-900">
                                            <FaCheck className="h-2.5 w-2.5 text-pri-white" />
                                          </div>
                                        );
                                      case 'declined':
                                      case 'rejected':
                                        return (
                                          <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center shadow-lg z-20 border-2 border-gray-900">
                                            <FaTimes className="h-2.5 w-2.5 text-pri-white" />
                                          </div>
                                        );
                                      default:
                                        return null;
                                    }
                                  })()}
                                </div>
                                
                                {/* Enhanced tooltip on hover with better visibility */}
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-max opacity-0 group-hover:opacity-100 group-hover:-bottom-9 transition-all duration-200 pointer-events-none z-50">
                                  <div className={`
                                    ${user.status === 'owner' || user.isSender || user.isOrganizer
                                      ? 'bg-amber-600 text-white'
                                      : 'bg-gray-700 text-white'} 
                                    px-3 py-1.5 rounded-lg text-xs font-medium backdrop-blur-sm shadow-xl whitespace-nowrap border border-white/10`}>
                                    {user.name ? user.name : (formatUserId(user.id) || 'Unknown User')}
                                    {(user.isSender || user.status === 'owner' || user.isOrganizer) && (
                                      <span className="ml-1.5 text-amber-200 text-[10px]">
                                        (Organizer)
                                      </span>
                                    )}
                                  </div>
                                  <div className={`w-2 h-2 rotate-45 absolute -top-1 left-1/2 -translate-x-1/2
                                    ${user.status === 'owner' || user.isSender || user.isOrganizer ? 'bg-amber-600' : 'bg-gray-700'}`}></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-3 text-gray-400 text-sm">
                            No shared user details available
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Action buttons */}
            <motion.div variants={itemVariants} className="flex justify-center pt-5 border-t border-gray-700/50 gap-4">
              {/* Only show edit & delete options for events the user owns */}
              {isEventOwner && (
                <>
                  <motion.button
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    className="flex flex-col items-center text-gray-300 hover:text-pri-white transition-all"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Initiating edit for event:', event.id);
                      // Close this dialog first
                      onClose();
                      // Then call the edit handler
                      if (onEdit) onEdit(event);
                    }}
                  >
                    <div className="p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 backdrop-blur-sm mb-1.5 border border-gray-600 shadow-md hover:shadow-lg hover:border-gray-500">
                      <FaEdit className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-medium">Edit</span>
                  </motion.button>
                  
                  <motion.button
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    className="flex flex-col items-center text-gray-300 hover:text-red-400 transition-all"
                    onClick={() => setShowConfirmDelete(true)}
                  >
                    <div className="p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 backdrop-blur-sm mb-1.5 border border-gray-600 shadow-md hover:shadow-lg hover:border-red-500/30">
                      <FaTrashAlt className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-medium">Delete</span>
                  </motion.button>
                </>
              )}
              
              {/* Only show Share button if NOT a shared event */}
              {!isShared && (
                <>
                  <motion.button
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    className="flex flex-col items-center text-gray-300 hover:text-mint-green transition-all"
                    onClick={() => onShare && onShare(event)}
                  >
                    <div className="p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 backdrop-blur-sm mb-1.5 border border-gray-600 shadow-md hover:shadow-lg hover:border-mint-green/50">
                      <FaShareAlt className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-medium">Share</span>
                  </motion.button>
                </>
              )}
            </motion.div>
          </div>

          {/* Delete confirmation dialog */}
          <AnimatePresence>
            {showConfirmDelete && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-10"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 rounded-xl p-6 w-5/6 max-w-sm border border-gray-700/50 shadow-2xl"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-900/50 rounded-lg text-red-400 border border-red-700/30">
                      <FaTrashAlt className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold text-pri-white">Delete Event?</h3>
                  </div>
                  
                  <p className="text-pri-white/90 text-sm leading-relaxed mb-6">
                    Are you sure you want to delete "<span className="text-pri-white font-bold">{event.title}</span>"? This action cannot be undone.
                  </p>
                  
                  <div className="flex justify-end space-x-3">
                    <motion.button
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      className="px-4 py-2 rounded-lg bg-gray-800 text-pri-white border border-gray-700 hover:bg-gray-700 hover:border-gray-600 transition-colors"
                      onClick={() => setShowConfirmDelete(false)}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      className="px-4 py-2 rounded-lg bg-red-900/30 text-red-400 border border-red-500/30 hover:bg-red-900/50 hover:border-red-500/50 transition-colors"
                      onClick={() => {
                        onDelete && onDelete(event);
                        setShowConfirmDelete(false);
                      }}
                    >
                      Delete
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EventDetailsDialog; 